import API from './api';
import { SIGNED_OUT_LINE, isSignedOutError } from '../utils/auth';

/**
 * Test-system routes on the candidate-portal backend.
 *
 * The catalog gate is entirely server-side: gated cycles simply are not in the
 * response for candidates without an approved application, so nothing here
 * filters or hides anything — it renders what it is given.
 */

export const getTestCatalog = async () => {
    const response = await API.get('test-catalog');
    return response.data?.cycles || [];
};

export const getTestCycle = async (cycleId) => {
    const response = await API.get(`test-cycles/${cycleId}`);
    return response.data?.cycle || null;
};

/** Spend one entry on a cycle. Returns the new enrolment and entries balance. */
export const enrolWithEntry = async (cycleId) => {
    const response = await API.post(`test-cycles/${cycleId}/enrol`);
    return response.data;
};

/**
 * The entry packs on sale with their live prices, read before Stripe's page.
 * Answers `{packs, available, reason}`; see lib/entryPacks for the shape.
 */
export const getEntryPacks = async () => {
    const response = await API.get('entries/packs');
    return response.data;
};

/**
 * Buy a pack and hold a spot in one go. Returns a Stripe Checkout url the caller
 * redirects to; the enrolment itself lands on the webhook side, spending one of
 * the pack's entries. The server checks the pack against its allow-list.
 */
export const startCycleCheckout = async (cycleId, productId) => {
    const response = await API.post(`test-cycles/${cycleId}/checkout`, { product_id: productId });
    return response.data;
};

/**
 * Buy entries without enrolling on anything. Same redirect contract as a cycle
 * checkout; the pack is one the packs read offered, validated again server-side.
 */
export const startEntriesCheckout = async (productId) => {
    const response = await API.post('entries/checkout', { product_id: productId });
    return response.data;
};

/**
 * Called by the cancel return page so an abandoned session — and the spot it may
 * be holding — is released now rather than when Stripe times it out. Safe twice.
 */
export const cancelCheckout = async (sessionId) => {
    const response = await API.post(`checkout/${sessionId}/cancelled`);
    return response.data;
};

export const getMyEntries = async () => {
    const response = await API.get('me/entries');
    return response.data;
};

export const getMyEnrolments = async () => {
    const response = await API.get('me/enrolments');
    return response.data?.enrolments || [];
};

/** Confirm participation during a cycle's confirmation window. */
export const confirmEnrolment = async (cycleId) => {
    const response = await API.post(`enrolments/${cycleId}/confirm`);
    return response.data;
};

/**
 * The candidate's certificates. Active ones carry preview_url and pdf_url — 15
 * minute signed links, so they are only good for this page load.
 */
export const getMyCertificates = async () => {
    const response = await API.get('me/certificates');
    return response.data?.certificates || [];
};

/** Switch a certificate's public link on or off. */
export const setCertificateSharing = async (certificateId, shareEnabled) => {
    const response = await API.post(`me/certificates/${certificateId}/share`, {
        share_enabled: shareEnabled,
    });
    return response.data?.certificate || null;
};

/**
 * The published application form plus the candidate's own application, if they
 * have one. The backend projects every unapproved state to `pending`, so this is
 * the only application state the portal can ever see besides `approved`.
 */
export const getTestApplication = async () => {
    const response = await API.get('application');
    return {
        form: response.data?.form || null,
        application: response.data?.application || null,
    };
};

/** Apply once. Answers are keyed by question_id; the form version is stamped server-side. */
export const submitTestApplication = async (answers) => {
    const response = await API.post('application', { answers });
    return response.data?.application || null;
};

/**
 * The backend answers a refused action with a human sentence plus a code; the
 * sentence is what a candidate should read, the code is for branching.
 */
export const readApiError = (error, fallback = 'Something went wrong. Please try again.') => {
    if (isSignedOutError(error)) return { message: SIGNED_OUT_LINE, code: 'signed_out' };
    const data = error?.response?.data;
    return {
        message: (typeof data === 'string' ? data : data?.error) || error?.message || fallback,
        code: data?.code || '',
    };
};
